// Phase MHM-2 · SLA Alert Badge — pulsing bell in MHM portal nav.
import { useEffect, useState, useCallback } from "react";
import { Bell, X, AlertOctagon, AlertTriangle, Clock, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import TrackingIdLink from "./TrackingIdLink";

const SEV_COLOR = { red: "bg-rose-500", yellow: "bg-amber-500" };
const SEV_ICON = { red: AlertOctagon, yellow: AlertTriangle };

export default function SlaAlertBadge() {
  const [counts, setCounts] = useState({ red: 0, yellow: 0, active: 0 });
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hub/sla/alerts?status=active");
      setRows(data.rows || []);
      setCounts(data.counts || {});
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const resolve = async (a) => {
    const note = window.prompt("Resolution note:");
    if (!note) return;
    try {
      await api.post(`/hub/sla/alerts/${a.alert_id}/resolve`, { resolution_note: note });
      toast.success("Resolved"); load();
    } catch (e) { toast.error(e.userMessage || "Resolve failed"); }
  };
  const snooze = async (a, minutes) => {
    try {
      await api.post(`/hub/sla/alerts/${a.alert_id}/snooze`, { minutes });
      toast.success(`Snoozed ${minutes} min`); load();
    } catch (e) { toast.error(e.userMessage || "Snooze failed"); }
  };

  const hasRed = (counts.red || 0) > 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="mhm-sla-bell"
        className="relative inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-slate-100"
        title="SLA alerts"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {counts.active > 0 && (
          <span
            data-testid="mhm-sla-bell-badge"
            className={`absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] text-white rounded-full ${
              hasRed ? "bg-rose-500 animate-pulse" : "bg-amber-500"
            }`}
          >
            {counts.active}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={() => setOpen(false)} data-testid="mhm-sla-panel">
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
              <h3 className="font-['Outfit'] text-lg font-semibold">
                SLA Alerts · {counts.active || 0}
              </h3>
              <button onClick={() => setOpen(false)} data-testid="mhm-sla-panel-close">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {rows.length === 0 && (
                <div className="text-sm text-green-600 py-10 text-center">
                  <Check className="h-6 w-6 mx-auto mb-1" />
                  No active alerts
                </div>
              )}
              {rows.map((a) => {
                const Icon = SEV_ICON[a.severity] || AlertTriangle;
                return (
                  <div key={a.alert_id} data-testid={`mhm-sla-alert-${a.alert_id}`}
                       className="border border-slate-200 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <span className={`inline-flex h-6 w-6 rounded-full ${SEV_COLOR[a.severity] || "bg-slate-400"} items-center justify-center text-white flex-shrink-0`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">{a.message}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          <span className="uppercase mr-2">{a.type}</span>
                          <span>{(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
                        </div>
                        <div className="mt-2 flex gap-2">
                          <button onClick={() => resolve(a)}
                                  data-testid={`mhm-sla-resolve-${a.alert_id}`}
                                  className="text-xs px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded">
                            Resolve
                          </button>
                          <button onClick={() => snooze(a, 30)}
                                  data-testid={`mhm-sla-snooze30-${a.alert_id}`}
                                  className="text-xs px-2 py-1 border border-slate-200 hover:bg-slate-50 rounded inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" /> 30m
                          </button>
                          <button onClick={() => snooze(a, 120)}
                                  className="text-xs px-2 py-1 border border-slate-200 hover:bg-slate-50 rounded inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" /> 2h
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
